/* eslint-disable max-classes-per-file */
// Compact Event logging format for efficient transmission and storage
// This file contains all compact format definitions, encoders, and utilities

import type {
  KeyboardEvent,
  MouseEvent,
  WheelEvent,
  TouchEvent,
  FocusEvent,
  FormEvent,
  ClipboardEvent,
  SelectionEvent,
  ViewportEvent,
  CompositionEvent,
  DeviceOrientationEvent,
  GlobalErrorEventLog,
} from './eventLogs';
import { getTimestampMicroseconds } from './useInputLogger';

// ===== LOOKUP DICTIONARIES =====

// Event type codes
export const EVENT_TYPE_CODES = {
  // Keyboard events
  keydown: 0,
  keyup: 1,

  // Mouse events
  mousedown: 2,
  mouseup: 3,
  click: 4,
  mousemove: 5,
  scroll: 6,
  contextmenu: 7,
  mouseleave: 8,
  mouseenter: 9,

  // Wheel events
  wheel: 10,

  // Touch events
  touchstart: 11,
  touchend: 12,
  touchmove: 13,

  // Focus events
  focus: 14,
  blur: 15,

  // Form events
  input: 16,
  change: 17,

  // Clipboard events
  copy: 18,
  paste: 19,
  cut: 20,

  // Selection events
  selectstart: 21,
  selectend: 22,
  selectionchange: 23,

  // Viewport events
  resize: 24,
  visibilitychange: 25,
  load: 26,
  beforeunload: 27,

  // Composition events
  compositionstart: 28,
  compositionend: 29,
  compositionupdate: 30,

  // Device orientation events
  deviceorientation: 31,
  devicemotion: 32,

  // Error events
  error: 33,
  unhandledrejection: 34,
} as const;

// Note: Mouse button codes are now stored as raw integers (0=left, 1=middle, 2=right) per browser spec

// Modifier key mapping for compact representation
export const MODIFIER_KEYS = {
  Alt: 'A',
  AltGraph: 'G',
  CapsLock: 'C',
  Control: 'X',
  Fn: 'F',
  FnLock: 'L',
  Hyper: 'H',
  Meta: 'M',
  NumLock: 'N',
  ScrollLock: 'S',
  Shift: 'T',
  Super: 'U',
  Symbol: 'Y',
  SymbolLock: 'Z',
} as const;

// Reverse lookup maps for decoding
export const CODE_TO_EVENT_TYPE = Object.fromEntries(
  Object.entries(EVENT_TYPE_CODES).map(([key, value]) => [value, key])
) as Record<number, keyof typeof EVENT_TYPE_CODES>;

export const MODIFIER_TO_KEY = Object.fromEntries(
  Object.entries(MODIFIER_KEYS).map(([key, value]) => [value, key])
) as Record<string, keyof typeof MODIFIER_KEYS>;

// ===== UTILITY FUNCTIONS =====

// Helper function to encode modifiers as a single string
export const encodeModifiers = (modifiers: string[]): string => {
  return modifiers
    .map((mod) => MODIFIER_KEYS[mod as keyof typeof MODIFIER_KEYS])
    .filter(Boolean)
    .sort()
    .join('');
};

// Helper function to decode modifiers from a string
export const decodeModifiers = (modifierString: string): string[] => {
  return modifierString
    .split('')
    .map((char) => MODIFIER_TO_KEY[char])
    .filter(Boolean);
};

// ===== ELEMENT LOG SYSTEM =====

/**
 * Element entry stored in the ElementLog array
 * [index, timestamp, tag_name, type, name, role, bounds_x, bounds_y, bounds_width, bounds_height]
 */
export type ElementEntry = [
  number, // index (stored in the array itself for memory-safe truncation)
  number, // timestamp
  string, // tag_name (lowercase for consistency, e.g., 'input', 'button', 'div')
  string | null, // type attribute (e.g., 'text', 'email', 'submit')
  string | null, // name attribute (form field identifier)
  string | null, // role attribute (accessibility role)
  number | null, // bounds_x
  number | null, // bounds_y
  number | null, // bounds_width
  number | null // bounds_height
];

/**
 * ElementLog manages a shared array of unique element descriptions and bounds
 * to reduce memory usage by storing only indices in event logs
 */
export class ElementLog {
  private elements: ElementEntry[] = [];

  private lookupTable: Map<string, number> = new Map();

  private nextIndex = 0;

  /**
   * Creates a lookup key from element attributes and bounds
   */
  static createLookupKey(
    attributes: {
      tagName: string;
      type: string | null;
      name: string | null;
      role: string | null;
    },
    bounds?: { x: number; y: number; width: number; height: number } | null
  ): string {
    const attributesKey = `${attributes.tagName}|${attributes.type}|${attributes.name}|${attributes.role}`;
    if (!bounds) {
      return `${attributesKey}|null`;
    }
    return `${attributesKey}|${bounds.x},${bounds.y},${bounds.width},${bounds.height}`;
  }

  /**
   * Update the element log with a new target element
   * Returns the index to use in event logs
   */
  update(target: EventTarget | null): number {
    // Get element attributes
    const attributes = ElementLog.getElementAttributes(target);

    // Get element bounds
    const bounds = ElementLog.getElementBounds(target);

    // Create lookup key
    const lookupKey = ElementLog.createLookupKey(attributes, bounds);

    // Check if we already have this combination
    const existingIndex = this.lookupTable.get(lookupKey);
    if (existingIndex !== undefined) {
      return existingIndex;
    }

    // Create new entry
    const index = this.nextIndex;
    this.nextIndex += 1;
    const timestamp = getTimestampMicroseconds();
    const entry: ElementEntry = [
      index,
      timestamp,
      attributes.tagName,
      attributes.type,
      attributes.name,
      attributes.role,
      bounds?.x ?? null,
      bounds?.y ?? null,
      bounds?.width ?? null,
      bounds?.height ?? null,
    ];

    // Store in array and lookup table
    this.elements.push(entry);
    this.lookupTable.set(lookupKey, index);

    return index;
  }

  /**
   * Get element bounding box
   */
  static getElementBounds(
    target: EventTarget | null
  ): { x: number; y: number; width: number; height: number } | undefined {
    if (!target || !(target instanceof Element)) return undefined;

    const rect = target.getBoundingClientRect();
    return {
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    };
  }

  /**
   * Get element attributes without sensitive data
   */
  static getElementAttributes(target: EventTarget | null): {
    tagName: string;
    type: string | null;
    name: string | null;
    role: string | null;
  } {
    if (!target || !(target instanceof Element)) {
      return {
        tagName: 'unknown',
        type: null,
        name: null,
        role: null,
      };
    }

    return {
      tagName: target.tagName.toLowerCase(), // Lowercase for consistency with HTML convention
      type: target.getAttribute('type'),
      name: target.getAttribute('name'),
      role: target.getAttribute('role'),
    };
  }

  /**
   * Get the elements array for transmission to backend
   */
  getElements(): ElementEntry[] {
    return [...this.elements];
  }

  /**
   * Clear all stored elements and reset indices
   */
  clear(): void {
    this.elements = [];
    this.lookupTable.clear();
    this.nextIndex = 0;
  }

  /**
   * Get the current length of the elements array
   */
  get length(): number {
    return this.elements.length;
  }
}

// ===== COMPACT FORMAT TYPES =====

/**
 * Touch Point Compact Format:
 * [identifier, x, y, pressure?, radiusX?, radiusY?, rotationAngle?, altitudeAngle?, azimuthAngle?]
 */
export type CompactTouchPoint = [
  number, // identifier
  number, // x
  number, // y
  (number | null)?, // pressure (null if not available)
  (number | null)?, // radiusX
  (number | null)?, // radiusY
  (number | null)?, // rotationAngle
  (number | null)?, // altitudeAngle
  (number | null)? // azimuthAngle
];

/**
 * Keyboard Event Compact Format:
 * [type_code, timestamp, key, code, element_index, modifiers_string, repeat?]
 */
export type CompactKeyboardEvent = [
  number, // type_code (EVENT_TYPE_CODES)
  number, // timestamp
  string, // key
  string, // code
  number, // element_index (references ElementLog array)
  string, // modifiers (encoded as single string)
  (boolean | null)? // repeat (optional, omit if false)
];

/**
 * Mouse Event Compact Format:
 * [type_code, timestamp, x, y, element_index, button?, scroll_top?, scroll_left?]
 */
export type CompactMouseEvent = [
  number, // type_code
  number, // timestamp
  number, // x
  number, // y
  number, // element_index (references ElementLog array)
  (number | null)?, // button (0=left, 1=middle, 2=right, null for non-click events)
  (number | null)?, // scroll_top (null if not scroll)
  (number | null)? // scroll_left
];

/**
 * Wheel Event Compact Format:
 * [type_code, timestamp, x, y, deltaX, deltaY, deltaZ, deltaMode, element_index]
 */
export type CompactWheelEvent = [
  number, // type_code
  number, // timestamp
  number, // x
  number, // y
  number, // deltaX
  number, // deltaY
  number, // deltaZ
  number, // deltaMode
  number // element_index (references ElementLog array)
];

/**
 * Touch Event Compact Format:
 * [type_code, timestamp, touches, changed_touches, target_touches, element_index, modifiers_string, touch_count]
 */
export type CompactTouchEvent = [
  number, // type_code
  number, // timestamp
  CompactTouchPoint[], // touches
  CompactTouchPoint[], // changed_touches
  CompactTouchPoint[], // target_touches
  number, // element_index (references ElementLog array)
  string, // modifiers (encoded)
  number // touch_count
];

/**
 * Focus Event Compact Format:
 * [type_code, timestamp, element_index]
 */
export type CompactFocusEvent = [
  number, // type_code
  number, // timestamp
  number // element_index (references ElementLog array)
];

/**
 * Form Event Compact Format:
 * [type_code, timestamp, element_index, value_length]
 */
export type CompactFormEvent = [
  number, // type_code
  number, // timestamp
  number, // element_index (references ElementLog array)
  number // value_length
];

/**
 * Clipboard Event Compact Format:
 * [type_code, timestamp, element_index, data_length?]
 */
export type CompactClipboardEvent = [
  number, // type_code
  number, // timestamp
  number, // element_index (references ElementLog array)
  (number | null)? // data_length (null if not available)
];

/**
 * Selection Event Compact Format:
 * [type_code, timestamp, element_index, selection_length?, selection_start?, selection_end?]
 */
export type CompactSelectionEvent = [
  number, // type_code
  number, // timestamp
  number, // element_index (references ElementLog array)
  (number | null)?, // selection_length (null if not available)
  (number | null)?, // selection_start
  (number | null)? // selection_end
];

/**
 * Viewport Event Compact Format:
 * [type_code, timestamp, viewport_width?, viewport_height?, visibility_state?, zoom_level?]
 */
export type CompactViewportEvent = [
  number, // type_code
  number, // timestamp
  (number | null)?, // viewport_width (null if not available)
  (number | null)?, // viewport_height
  (string | null)?, // visibility_state
  (number | null)? // zoom_level
];

/**
 * Composition Event Compact Format:
 * [type_code, timestamp, element_index, data_length?]
 */
export type CompactCompositionEvent = [
  number, // type_code
  number, // timestamp
  number, // element_index (references ElementLog array)
  (number | null)? // data_length (null if not available)
];

/**
 * Device Orientation Event Compact Format:
 * [type_code, timestamp, alpha?, beta?, gamma?, acc_x?, acc_y?, acc_z?, rot_alpha?, rot_beta?, rot_gamma?]
 */
export type CompactDeviceOrientationEvent = [
  number, // type_code
  number, // timestamp
  (number | null)?, // alpha (null if not available)
  (number | null)?, // beta
  (number | null)?, // gamma
  (number | null)?, // acceleration.x (null if not devicemotion)
  (number | null)?, // acceleration.y
  (number | null)?, // acceleration.z
  (number | null)?, // rotation_rate.alpha
  (number | null)?, // rotation_rate.beta
  (number | null)? // rotation_rate.gamma
];

/**
 * Error Event Compact Format:
 * [type_code, timestamp, message_length?]
 */
export type CompactErrorEvent = [
  number, // type_code
  number, // timestamp
  (number | null)? // message_length (null if not available)
];

// ===== ENCODER FUNCTIONS =====
/**
 * Removes trailing null values from an array to minimize payload size
 * @param arr - Array that may have trailing null values
 * @returns Array with trailing nulls removed
 */
export const trimTrailingNulls = <T>(arr: T[]): T[] => {
  let lastIndex = arr.length - 1;

  // Find the last non-null element
  while (lastIndex >= 0 && arr[lastIndex] === null) {
    lastIndex -= 1;
  }

  // Return slice up to (and including) the last non-null element
  return arr.slice(0, lastIndex + 1);
};

// Touch point helpers
const encodeTouchPoint = (touch: any): CompactTouchPoint => {
  const result: CompactTouchPoint = [touch.identifier, touch.x, touch.y];

  // The ?? operator is the nullish coalescing operator - it returns the right-hand side
  // when the left-hand side is null or undefined, otherwise returns the left-hand side
  result.push(touch.pressure ?? null);
  result.push(touch.radiusX ?? null);
  result.push(touch.radiusY ?? null);
  result.push(touch.rotationAngle ?? null);
  result.push(touch.altitudeAngle ?? null);
  result.push(touch.azimuthAngle ?? null);

  return result;
};

export const encodeKeyboardEvent = (
  event: KeyboardEvent
): CompactKeyboardEvent => {
  const result: CompactKeyboardEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.key,
    event.code,
    event.element_index,
    encodeModifiers(event.modifiers),
  ];

  // Only include repeat if it's true
  if (event.repeat) {
    result.push(event.repeat);
  }

  return result;
};

export const encodeMouseEvent = (event: MouseEvent): CompactMouseEvent => {
  const result: CompactMouseEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.x,
    event.y,
    event.element_index,
  ];

  // Add optional fields
  result.push(event.button ?? null);
  result.push(event.scroll_top ?? null);
  result.push(event.scroll_left ?? null);

  return result;
};

export const encodeWheelEvent = (event: WheelEvent): CompactWheelEvent => {
  const result: CompactWheelEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.x,
    event.y,
    event.deltaX,
    event.deltaY,
    event.deltaZ,
    event.deltaMode,
    event.element_index,
  ];

  return result;
};

export const encodeTouchEvent = (event: TouchEvent): CompactTouchEvent => {
  const result: CompactTouchEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.touches.map(encodeTouchPoint),
    event.changed_touches.map(encodeTouchPoint),
    event.target_touches.map(encodeTouchPoint),
    event.element_index,
    encodeModifiers(event.modifiers),
    event.touch_count,
  ];

  return result;
};

export const encodeFocusEvent = (event: FocusEvent): CompactFocusEvent => {
  const result: CompactFocusEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.element_index,
  ];

  return result;
};

export const encodeFormEvent = (event: FormEvent): CompactFormEvent => {
  const result: CompactFormEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.element_index,
    event.value_length,
  ];

  return result;
};

export const encodeClipboardEvent = (
  event: ClipboardEvent
): CompactClipboardEvent => {
  const result: CompactClipboardEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.element_index,
  ];

  result.push(event.data_length ?? null);

  return result;
};

export const encodeSelectionEvent = (
  event: SelectionEvent
): CompactSelectionEvent => {
  const result: CompactSelectionEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.element_index,
  ];

  result.push(event.selection_length ?? null);
  result.push(event.selection_start ?? null);
  result.push(event.selection_end ?? null);

  return result;
};

export const encodeViewportEvent = (
  event: ViewportEvent
): CompactViewportEvent => {
  const result: CompactViewportEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
  ];

  result.push(event.viewport_width ?? null);
  result.push(event.viewport_height ?? null);
  result.push(event.visibility_state ?? null);
  result.push(event.zoom_level ?? null);

  return result;
};

export const encodeCompositionEvent = (
  event: CompositionEvent
): CompactCompositionEvent => {
  const result: CompactCompositionEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
    event.element_index,
  ];

  result.push(event.data_length ?? null);

  return result;
};

export const encodeDeviceOrientationEvent = (
  event: DeviceOrientationEvent
): CompactDeviceOrientationEvent => {
  const result: CompactDeviceOrientationEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
  ];

  result.push(event.alpha ?? null);
  result.push(event.beta ?? null);
  result.push(event.gamma ?? null);
  result.push(event.acceleration?.x ?? null);
  result.push(event.acceleration?.y ?? null);
  result.push(event.acceleration?.z ?? null);
  result.push(event.rotation_rate?.alpha ?? null);
  result.push(event.rotation_rate?.beta ?? null);
  result.push(event.rotation_rate?.gamma ?? null);

  return result;
};

export const encodeErrorEvent = (
  event: GlobalErrorEventLog
): CompactErrorEvent => {
  const result: CompactErrorEvent = [
    EVENT_TYPE_CODES[event.type],
    event.timestamp,
  ];

  if (event.message_length !== undefined) {
    result.push(event.message_length);
  }

  return result;
};
